// client/src/pages/Home/EntryScreen.jsx
// Landing v2 « Arcane Nocturne » : hero cinématique, orbes élémentaires,
// CTAs contextuels. Logique d'entrée (audio, transition) conservée.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudio } from '../../hooks/useAudio';
import { useAuth } from '../../hooks/useAuth';
import Icon from '../../components/ui/Icon';
import './EntryScreen.css';
import homeBackground from '../../assets/images/HomeScreen.png';
import elementumLogo from '../../assets/images/Elementum-logo.png';

const ELEMENTS = [
  { key: 'fire', label: 'Feu', glyph: '🔥' },
  { key: 'water', label: 'Eau', glyph: '💧' },
  { key: 'earth', label: 'Terre', glyph: '🌿' },
  { key: 'light', label: 'Lumière', glyph: '✨' },
  { key: 'dark', label: 'Ténèbres', glyph: '🌑' },
  { key: 'rainbow', label: 'Novarcane', glyph: '🌈' },
];

const EntryScreen = () => {
  const { resumeAudioContext, hasError } = useAudio();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [animationStarted, setAnimationStarted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimationStarted(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const isLogged = currentUser && !currentUser.isGuest;

  const enterGame = () => {
    setIsTransitioning(true);
    resumeAudioContext();
    setTimeout(() => {
      navigate('/dashboard');
    }, 900);
  };

  const goTo = (path) => {
    resumeAudioContext();
    navigate(path);
  };

  return (
    <div
      className={`entry-screen-container ${isTransitioning ? 'fade-transition' : ''}`}
      style={{
        backgroundImage: `url(${homeBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      <div className="entry-veil" aria-hidden="true" />
      <div className="entry-vignette" aria-hidden="true" />

      {/* Orbes élémentaires flottantes */}
      <div className="floating-elements" aria-hidden="true">
        <div className="orb orb-fire" />
        <div className="orb orb-water" />
        <div className="orb orb-earth" />
        <div className="orb orb-light" />
        <div className="orb orb-dark" />
        <div className="orb orb-rainbow" />
      </div>

      {/* Barre landing */}
      <header className={`entry-topbar ${animationStarted ? 'in' : ''}`}>
        <div className="entry-brand">
          <span className="entry-brand-sigil"><span>E</span></span>
          <span className="entry-brand-name">ELEMENTUM</span>
        </div>
        <nav className="entry-topnav">
          {!isLogged && (
            <button className="entry-toplink" onClick={() => goTo('/auth/login')}>
              Se connecter
            </button>
          )}
          {(!currentUser || currentUser.isGuest) && (
            <button className="entry-topcta" onClick={() => goTo('/auth/register')}>
              <Icon name="userplus" size={14} />
              Créer un compte
            </button>
          )}
        </nav>
      </header>

      <div className={`entrance-screen ${isTransitioning ? 'fading-out' : ''} ${animationStarted ? 'animated' : ''}`}>
        <div className="logo-container">
          <img src={elementumLogo} alt="Elementum : Novarcane Origins" className="game-logo" />
        </div>

        <p className="game-kicker">Novarcane Origins</p>
        <p className="game-subtitle">La conquête des six éléments commence</p>

        <div className="entry-divider" aria-hidden="true">
          <span /><i>◆</i><span />
        </div>

        <div className="entry-stats">
          <div className="entry-stat"><Icon name="cards" size={15} /><span>505 cartes</span></div>
          <div className="entry-stat-dot" />
          <div className="entry-stat"><Icon name="sparkles" size={15} /><span>6 éléments</span></div>
          <div className="entry-stat-dot" />
          <div className="entry-stat"><Icon name="play" size={15} /><span>Duels en ligne</span></div>
        </div>

        <button className="enter-game-button" onClick={enterGame}>
          <span className="button-text">{isLogged ? 'Reprendre le duel' : 'Entrer dans l\'arène'}</span>
          <span className="button-shine" aria-hidden="true" />
        </button>

        <div className="entry-secondary-actions">
          {(!currentUser || currentUser.isGuest) && (
            <button className="entry-secondary-button" onClick={() => goTo('/auth/register')}>
              <Icon name="userplus" size={15} />
              <span>Créer un compte{currentUser?.isGuest ? ' pour garder vos cartes' : ''}</span>
            </button>
          )}
          <button className="entry-secondary-button outline" onClick={() => goTo('/collection')}>
            <Icon name="collection" size={15} />
            <span>Découvrir les cartes</span>
          </button>
        </div>

        {/* Les six éléments */}
        <div className="entry-elements" aria-label="Les six éléments">
          {ELEMENTS.map((el, i) => (
            <div className={`entry-element el-${el.key}`} key={el.key} style={{ animationDelay: `${0.7 + i * 0.12}s` }}>
              <span className="entry-element-glyph" aria-hidden="true">{el.glyph}</span>
              <span className="entry-element-label">{el.label}</span>
            </div>
          ))}
        </div>

        {hasError && (
          <div className="audio-error-notice">
            Problème de chargement audio. Vous pourriez ne pas entendre de musique.
          </div>
        )}
      </div>

      <div className="entry-scrollhint" aria-hidden="true">
        <span className="entry-scrollhint-mouse"><span /></span>
      </div>
    </div>
  );
};

export default EntryScreen;
